import { useCallback, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ItemPageBaseQuery } from '../graphql/graphql';
import { getItemPageBase } from '../api/queries';
import { ItemStatusEnum } from '../graphql/types';

const isE2ETest = process.env.NEXT_PUBLIC_E2E === '1';
const NO_SSR_VERSION_TIME = 0;

export const useItemRevalidation = (
  itemId: string | undefined,
  remoteUpdatedAt: string,
  ssrVersion: string | null | undefined,
  nonSsrCurrentItemDataState: ItemStatusEnum | undefined,
  nonSsrCurrentItemUpdatedAt: string,
  nonSsrCurrentItemRefetch: () => void
) => {
  const { i18n } = useTranslation();
  const hasNoCache =
    !remoteUpdatedAt && nonSsrCurrentItemDataState === ItemStatusEnum.Published;
  const actualUpdatedAt = hasNoCache
    ? nonSsrCurrentItemUpdatedAt
    : remoteUpdatedAt;

  const [invalidateItemPageQuery, { data, loading }] =
    useLazyQuery<ItemPageBaseQuery>(getItemPageBase, {
      variables: { id: itemId },
      fetchPolicy: 'network-only'
    });

  const refetch = useCallback(() => {
    if (hasNoCache) nonSsrCurrentItemRefetch();
    else invalidateItemPageQuery();
  }, [hasNoCache, invalidateItemPageQuery, nonSsrCurrentItemRefetch]);

  useEffect(() => {
    if (!isE2ETest) {
      const remoteTime = new Date(actualUpdatedAt).getTime();
      const isrTime = ssrVersion
        ? new Date(ssrVersion).getTime()
        : NO_SSR_VERSION_TIME;

      if (remoteTime && remoteTime > isrTime) refetch();
    }
  }, [actualUpdatedAt, ssrVersion, itemId, i18n.language, refetch]);

  return {
    isDataRefreshing: loading,
    invalidateItemData: data
  };
};
